import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    List,
    ListItem,
    Typography
} from "@mui/material";
import {
    MeasurementUnit,
    SubmitQuickQuoteInput
} from "../../API";
import React, {
    useEffect,
    useState,
} from "react";

import { IssueCard } from "./IssueCard";
import { LoadingButton } from "@mui/lab";
import { PermissionResourceType } from "../../models";
import { TemporaryImage } from "../../componentsV2/image/TemporaryImage";
import { isMobile } from "react-device-detect";
import { useIssues } from "./contexts/IssuesContextProvider";
import { useSubmitDimensions } from "./data/hooks/useSubmitDimensions";
import { useUploadImages } from "../../componentsV2/image/carouselComponent/useUploadImage";

export type QuickQuoteModalProps = {
    open: boolean;
    onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
    issueIds: string[];
};

export const QuickQuoteModal = ({ open, onClose, issueIds }: QuickQuoteModalProps) => {
    const { submitQuickQuotes, parentIdToDimensionMap, issues, setIsQuickQuoteSubmitting, resourceIdToImageMap } = useIssues();
    const { submitDimensions } = useSubmitDimensions();
    const { uploadImages } = useUploadImages();

    const [isLoading, setIsLoading] = useState(false);
    const [issueIdToStagedImagesMap, setIssueIdToStagedImagesMap] = useState<Map<string, TemporaryImage[]>>(new Map());
    const [missingDimensionFormRefs, setMissingDimensionFormRefs] = useState<Map<string, React.RefObject<HTMLFormElement>>>(new Map());

    if (!issueIds) {
        return null;
    }

    const selectedIssues = issues.filter((issue) => issueIds.includes(issue.id));

    const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClose(e);
    };

    const handleStagedImagesChange = (issueId: string) => (value: TemporaryImage[] | ((prev: TemporaryImage[]) => TemporaryImage[])) => {
        setIssueIdToStagedImagesMap(prev => {
            const newMap = new Map(prev);
            const currentImages = newMap.get(issueId) ?? [];
            const newImages = typeof value === 'function' ? value(currentImages) : value;
            newMap.set(issueId, newImages);
            return newMap;
        });
    };

    useEffect(() => {
        selectedIssues.forEach(issue => {
            const existingDimension = parentIdToDimensionMap.get(issue.id);
            if ((!existingDimension || !existingDimension.measurement?.length) && !missingDimensionFormRefs.has(issue.id)) {
                setMissingDimensionFormRefs(prev => {
                    const newMap = new Map(prev);
                    newMap.set(issue.id, React.createRef<HTMLFormElement>());
                    return newMap;
                });
            }
        });
    }, [selectedIssues, parentIdToDimensionMap, missingDimensionFormRefs]);

    const getFormValues = (formData: FormData) => {
        return {
            length: parseFloat(formData.get("length") as string || "0"),
            width: parseFloat(formData.get("width") as string || "0"),
            measurementUnit: (formData.get("unit") as MeasurementUnit) || MeasurementUnit.METER
        };
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle>
                Quick Estimate
            </DialogTitle>
            <Typography variant="body1" mx={3}>
                Submit a request for a quick estimate for the selected issues, and we'll get back to you shortly.
            </Typography>
            <Box display="flex" flexDirection="column" alignItems="center">
                <List style={{ listStyleType: 'none', padding: 0, width: "100%" }}>
                    {selectedIssues.map((issue) => (
                        <ListItem
                            sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                paddingY: "2px"
                            }}
                            key={issue.id}
                        >
                            <IssueCard
                                issue={issue}
                                images={resourceIdToImageMap.get(issue.id) ?? []}
                                inFocus
                                hideQuickQuote
                                showDimensionsForm={missingDimensionFormRefs.has(issue.id)}
                                dimensionFormRef={missingDimensionFormRefs.get(issue.id)}
                                showUploadStagingImages={(resourceIdToImageMap.get(issue.id) ?? []).length === 0}
                                stagedImages={issueIdToStagedImagesMap.get(issue.id) ?? []}
                                setStagedImages={handleStagedImagesChange(issue.id)}
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    color="error"
                >
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isLoading}
                    onClick={async (e) => {
                        try {
                            setIsLoading(true);
                            setIsQuickQuoteSubmitting(true);

                            // Upload staged images for each issue
                            await Promise.all(
                                Array.from(issueIdToStagedImagesMap.entries())
                                    .flatMap(([issueId, images]) =>
                                        images.map(imageDTO =>
                                            uploadImages(
                                                [imageDTO.file],
                                                { shouldResize: true },
                                                { id: issueId, type: PermissionResourceType.ISSUE },
                                                issues.find(issue => issue.id === issueId)?.propertyId ?? ''
                                            )
                                        )
                                    )
                            );

                            // Submit all dimension forms
                            const updatedQuickQuotes: SubmitQuickQuoteInput[] = [];
                            await Promise.all(
                                Array.from(missingDimensionFormRefs.entries())
                                    .filter(([_, ref]) => ref?.current)
                                    .map(([issueId, ref]) => {
                                        if (!ref.current) return null;
                                        const formData = new FormData(ref.current);
                                        const formValues = getFormValues(formData);
                                        if (formValues.length > 0) {
                                            updatedQuickQuotes.push({
                                                issueId: issueId,
                                                length: formValues.length,
                                                width: formValues.width,
                                                measurementUnit: formValues.measurementUnit
                                            });
                                            return submitDimensions?.(issueId, formData);
                                        }
                                        return null;
                                    })
                            );

                            // Create quick quotes with form values
                            const quickQuotes = selectedIssues.map((issue) => {
                                const updatedQuickQuote = updatedQuickQuotes.find(quickQuote => quickQuote.issueId === issue.id);
                                if (updatedQuickQuote) {
                                    return updatedQuickQuote;
                                }

                                const existingDimension = parentIdToDimensionMap.get(issue.id);
                                if (existingDimension?.measurement?.length) {
                                    return {
                                        issueId: issue.id,
                                        length: existingDimension.measurement.length,
                                        width: existingDimension.measurement.width,
                                        measurementUnit: existingDimension.measurement.measurementUnit
                                    } satisfies SubmitQuickQuoteInput;
                                }

                                return {
                                    issueId: issue.id,
                                    length: null,
                                    width: null,
                                    measurementUnit: MeasurementUnit.METER
                                } satisfies SubmitQuickQuoteInput;
                            });

                            submitQuickQuotes?.(quickQuotes).then(() => setIsQuickQuoteSubmitting(false));
                            handleClose(e);
                        } catch (error) {
                            console.error("Error submitting quick quote:", error);
                            setIsQuickQuoteSubmitting(false);
                        } finally {
                            setIsLoading(false);
                        }
                    }}
                >
                    Start Quick Estimate
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};